/**
 * Client-side projection of `ClaimWorkflowHistoryItem` (Status / Step / Action tuple + timestamp)
 * into the Smart Tracker model: a five-node claim journey, an operational phase, a visual state
 * with overlays, an owner, and an evidence level.
 *
 * Source: claim-workflow-smart-tracker-spec.md (status baseline, step overrides, action outcomes,
 * compound rules). The history endpoint exposes only the tuple, its labels and a timestamp, so
 * everything here is derived from those. Anything that needs payer, transmission, payment or
 * message context is left unknown and tagged `CLAIM_CONTEXT_REQUIRED` / `UNAVAILABLE`, never guessed.
 */
import { ClaimWorkflowHistoryItem } from '../../api';

export type JourneyStage =
    | 'CLAIM_PREPARATION'
    | 'PRIMARY_COVERAGE'
    | 'ADDITIONAL_COVERAGE'
    | 'PATIENT_BALANCE'
    | 'CLOSURE'
    | 'UNCLASSIFIED';

export type OperationalPhase =
    | 'INTAKE'
    | 'CODING'
    | 'CLAIM_ASSEMBLY'
    | 'CLAIM_SPLITTING'
    | 'DOCUMENT_READINESS'
    | 'AUTHORIZATION'
    | 'COVERAGE_VERIFICATION'
    | 'SUBMISSION_READINESS'
    | 'ORIGINAL_SUBMISSION'
    | 'TRANSMISSION_STATUS'
    | 'TIMELY_FILING'
    | 'TRANSMISSION_FOLLOW_UP'
    | 'PAYER_REVIEW'
    | 'CLAIM_CORRECTION'
    | 'REJECTION_FOLLOW_UP'
    | 'DENIAL_FOLLOW_UP'
    | 'ADDITIONAL_PAYER_BILLING'
    | 'PATIENT_BILLING'
    | 'CLOSURE'
    | 'UNCLASSIFIED';

export type VisualState =
    | 'ACTIVE'
    | 'AWAITING_TRANSMISSION_STATUS'
    | 'AWAITING_PAYER'
    | 'SCHEDULED'
    | 'PROVIDER_ACTION_NEEDED'
    | 'ENTER_ACTION_NEEDED'
    | 'SYSTEM_BLOCKED'
    | 'RETRYING'
    | 'ACTION_REQUIRED_AFTER_RETRIES'
    | 'COMPLETED'
    | 'BYPASSED'
    | 'UNCLASSIFIED';

export type EventOverlay = 'PAUSED' | 'REWORK';
export type PayerLevel = 'NONE' | 'PRIMARY' | 'SECONDARY' | 'TERTIARY' | 'TERMINAL' | 'UNKNOWN';
export type EventOwner = 'ENTER' | 'PROVIDER' | 'PAYER' | 'GUARANTOR' | 'SYSTEM' | 'NONE' | 'UNKNOWN';
export type EvidenceLevel = 'TUPLE_PROVEN' | 'HISTORY_DERIVED' | 'CLAIM_CONTEXT_REQUIRED' | 'UNAVAILABLE';

export type StageDisplayState =
    | 'NOT_APPLICABLE'
    | 'PENDING'
    | 'ACTIVE'
    | 'COMPLETE'
    | 'COMPLETE_REWORK_ACTIVE'
    | 'BLOCKED'
    | 'UNKNOWN';

export const JOURNEY_STAGES: readonly JourneyStage[] = [
    'CLAIM_PREPARATION',
    'PRIMARY_COVERAGE',
    'ADDITIONAL_COVERAGE',
    'PATIENT_BALANCE',
    'CLOSURE',
];

export const JOURNEY_STAGE_LABEL: Record<JourneyStage, string> = {
    CLAIM_PREPARATION: 'Claim Preparation',
    PRIMARY_COVERAGE: 'Primary Coverage',
    ADDITIONAL_COVERAGE: 'Additional Coverage',
    PATIENT_BALANCE: 'Patient Balance',
    CLOSURE: 'Complete',
    UNCLASSIFIED: 'Unclassified',
};

/** Stage names in running copy ("Returned to …"): the final node reads "Complete" on the chevron but "Closure" in prose. */
const STAGE_EYEBROW_LABEL: Record<JourneyStage, string> = { ...JOURNEY_STAGE_LABEL, CLOSURE: 'Closure' };

export const PHASE_LABEL: Record<OperationalPhase, string> = {
    INTAKE: 'Intake',
    CODING: 'Coding',
    CLAIM_ASSEMBLY: 'Claim Assembly',
    CLAIM_SPLITTING: 'Claim Splitting',
    DOCUMENT_READINESS: 'Document Readiness',
    AUTHORIZATION: 'Authorization',
    COVERAGE_VERIFICATION: 'Coverage Verification',
    SUBMISSION_READINESS: 'Submission Readiness',
    ORIGINAL_SUBMISSION: 'Original Submission',
    TRANSMISSION_STATUS: 'Transmission Status',
    TIMELY_FILING: 'Timely Filing',
    TRANSMISSION_FOLLOW_UP: 'Transmission Follow-up',
    PAYER_REVIEW: 'Payer Review',
    CLAIM_CORRECTION: 'Claim Correction',
    REJECTION_FOLLOW_UP: 'Rejection Follow-up',
    DENIAL_FOLLOW_UP: 'Denial Follow-up',
    ADDITIONAL_PAYER_BILLING: 'Additional-Payer Billing',
    PATIENT_BILLING: 'Patient Billing',
    CLOSURE: 'Closure',
    UNCLASSIFIED: 'Unclassified',
};

interface IStatusBaseline {
    stage: JourneyStage;
    phase: OperationalPhase;
    payer: PayerLevel;
    label: string;
}

const base = (stage: JourneyStage, phase: OperationalPhase, payer: PayerLevel, label: string): IStatusBaseline => ({
    stage,
    phase,
    payer,
    label,
});

/** Spec "Status baseline mapping". Keys are exact enum values. */
const STATUS_BASELINE: Record<string, IStatusBaseline> = {
    EMR_WAIT: base('CLAIM_PREPARATION', 'INTAKE', 'NONE', 'Waiting for EMR update'),
    NEEDS_ENTER_CODING: base('CLAIM_PREPARATION', 'CODING', 'NONE', 'Preparing claim coding'),
    NEEDS_VENDOR_CODING: base('CLAIM_PREPARATION', 'CODING', 'NONE', 'Vendor coding requested'),
    EXECUTE_ENTER_CODING: base('CLAIM_PREPARATION', 'CODING', 'NONE', 'Applying claim coding'),
    WAIT_FOR_PROFESSIONAL_CREATED: base('CLAIM_PREPARATION', 'CLAIM_ASSEMBLY', 'NONE', 'Creating billable claim'),
    CLAIM_SPLIT: base('CLAIM_PREPARATION', 'CLAIM_SPLITTING', 'NONE', 'Splitting claim by payer instructions'),
    HIGH_VALUE_CLAIM_SPLIT: base('CLAIM_PREPARATION', 'CLAIM_SPLITTING', 'NONE', 'Splitting high-value claim'),
    NEED_CLAIM_DOCUMENTS: base('CLAIM_PREPARATION', 'DOCUMENT_READINESS', 'NONE', 'Importing initial claim documents'),
    NEED_MEDICAL_RECORDS: base(
        'PRIMARY_COVERAGE',
        'DOCUMENT_READINESS',
        'PRIMARY',
        'Medical records needed before billing',
    ),
    NEED_PRIMARY_AUTHORIZATION_NUMBER: base(
        'PRIMARY_COVERAGE',
        'AUTHORIZATION',
        'PRIMARY',
        'Primary authorization needed',
    ),
    NEED_PRIMARY_ELIGIBILITY_CHECK: base(
        'PRIMARY_COVERAGE',
        'COVERAGE_VERIFICATION',
        'PRIMARY',
        'Verifying primary coverage',
    ),
    TIMELY_FILING: base('PRIMARY_COVERAGE', 'TIMELY_FILING', 'PRIMARY', 'Protecting the primary filing deadline'),
    NEED_PRIMARY_TRANSMIT: base(
        'PRIMARY_COVERAGE',
        'ORIGINAL_SUBMISSION',
        'PRIMARY',
        'Preparing primary claim submission',
    ),
    NEED_PRIMARY_NON_ACKNOWLEDGED_TRANSMIT: base(
        'PRIMARY_COVERAGE',
        'TRANSMISSION_FOLLOW_UP',
        'PRIMARY',
        'Following up on stalled primary transmission status',
    ),
    REJECTION_UNENROLLED_PAYER: base(
        'PRIMARY_COVERAGE',
        'REJECTION_FOLLOW_UP',
        'PRIMARY',
        'Enrollment required after rejection',
    ),
    PROVIDER_REJECTION_WORKER: base(
        'PRIMARY_COVERAGE',
        'REJECTION_FOLLOW_UP',
        'PRIMARY',
        'Working configured primary rejection rule',
    ),
    // Payer-neutral until payment / transmission context identifies the coverage level.
    // REJECTED_CLAIM / DENIED_CLAIM / WAITING_FOR_PAYMENT say nothing about WHICH coverage they belong to.
    // They start with an unknown stage and payer; history or the claim's payer count resolves them (NS-3).
    REJECTED_CLAIM: base('UNCLASSIFIED', 'REJECTION_FOLLOW_UP', 'UNKNOWN', 'Rejection needs Enter review'),
    NEED_PRIMARY_RETRANSMIT: base(
        'PRIMARY_COVERAGE',
        'CLAIM_CORRECTION',
        'PRIMARY',
        'Correcting or resubmitting primary claim',
    ),
    PAYMENT_DENIED_FOR_HOSPICE_CARE: base(
        'PRIMARY_COVERAGE',
        'DENIAL_FOLLOW_UP',
        'PRIMARY',
        'Correcting hospice-care denial',
    ),
    PAYMENT_DENIED_FOR_LACK_OF_DOCUMENTATION: base(
        'PRIMARY_COVERAGE',
        'DENIAL_FOLLOW_UP',
        'PRIMARY',
        'Responding to documentation denial',
    ),
    PAYMENT_DENIED_FOR_NEED_NEW_SUBMISSION: base(
        'PRIMARY_COVERAGE',
        'DENIAL_FOLLOW_UP',
        'PRIMARY',
        'Sending new original claim after denial',
    ),
    PROVIDER_DENIAL_WORKER: base(
        'PRIMARY_COVERAGE',
        'DENIAL_FOLLOW_UP',
        'PRIMARY',
        'Working configured primary denial rule',
    ),
    DENIED_CLAIM: base('UNCLASSIFIED', 'DENIAL_FOLLOW_UP', 'UNKNOWN', 'Denial needs Enter review'),
    NEED_SECONDARY_TRANSMIT: base(
        'ADDITIONAL_COVERAGE',
        'ADDITIONAL_PAYER_BILLING',
        'SECONDARY',
        'Coordinating secondary coverage',
    ),
    NEED_TERTIARY_TRANSMIT: base(
        'ADDITIONAL_COVERAGE',
        'ADDITIONAL_PAYER_BILLING',
        'TERTIARY',
        'Coordinating tertiary coverage',
    ),
    // Stage depends on how many payers are active today, which the history payload does not carry.
    WAITING_FOR_PAYMENT: base('UNCLASSIFIED', 'PAYER_REVIEW', 'UNKNOWN', 'Waiting for payer'),
    NEED_GUARANTOR_COLLECTIONS: base('PATIENT_BALANCE', 'PATIENT_BILLING', 'NONE', 'Preparing patient collections'),
    NEEDS_CLOSED: base('CLOSURE', 'CLOSURE', 'NONE', 'Administrative closure due'),
};

interface IStepRule {
    phase?: OperationalPhase;
    stage?: JourneyStage;
    owner?: EventOwner;
    visual?: VisualState;
    label?: string;
    rework?: string;
    /** Step keeps whatever stage/phase the status (or earlier history) established. */
    preserve?: boolean;
}

/** Spec "Step overrides". Steps identify waits, prerequisites and the operation requested. */
const STEP_RULES: Record<string, IStepRule> = {
    NEEDS_ENTER_CODING_FOR_LATEST_HL7: {
        stage: 'CLAIM_PREPARATION',
        phase: 'CODING',
        owner: 'ENTER',
        label: 'Coding needed for latest EMR update',
    },
    NEEDS_ENTER_CODING_FOR_MISSED_HL7: {
        stage: 'CLAIM_PREPARATION',
        phase: 'CODING',
        owner: 'ENTER',
        label: 'Re-coding missed EMR update',
        rework: 'New HL7 requires re-coding',
    },
    RUN_CODING: { stage: 'CLAIM_PREPARATION', phase: 'CODING', owner: 'SYSTEM', label: 'Running Enter coding' },
    WAITING_FOR_VALID_HL7: {
        stage: 'CLAIM_PREPARATION',
        phase: 'INTAKE',
        owner: 'PROVIDER',
        visual: 'PROVIDER_ACTION_NEEDED',
        label: 'Waiting for valid claim data',
    },
    WAITING_FOR_CLAIM_TAG: {
        stage: 'CLAIM_PREPARATION',
        phase: 'CODING',
        owner: 'UNKNOWN',
        label: 'Waiting for required claim tag',
    },
    CREATE_NEW_CODING: {
        stage: 'CLAIM_PREPARATION',
        phase: 'CLAIM_ASSEMBLY',
        owner: 'SYSTEM',
        label: 'Creating a new coding request',
        rework: 'Coding recreated after professional-creation delay',
    },
    WAITING_FOR_PROFESSIONAL_CREATED: {
        stage: 'CLAIM_PREPARATION',
        phase: 'CLAIM_ASSEMBLY',
        owner: 'SYSTEM',
        label: 'Creating billable claim',
    },
    SPLIT_CLAIM: { stage: 'CLAIM_PREPARATION', phase: 'CLAIM_SPLITTING', owner: 'SYSTEM', label: 'Splitting claim' },
    RUN_ELIGIBILITY_CHECK: {
        stage: 'PRIMARY_COVERAGE',
        phase: 'COVERAGE_VERIFICATION',
        owner: 'SYSTEM',
        label: 'Checking primary eligibility',
    },
    ANALYZE_ELIGIBILITY_RESPONSE: {
        stage: 'PRIMARY_COVERAGE',
        phase: 'COVERAGE_VERIFICATION',
        owner: 'SYSTEM',
        label: 'Reviewing eligibility response',
    },
    REOPEN_MESSAGE: { preserve: true, label: 'Follow-up task reopened' },
    CLOSE_MESSAGES: { preserve: true, owner: 'SYSTEM', label: 'Closing outdated or completed tasks' },
    CLOSE_MESSAGES_TO_SUPPORT: { preserve: true, owner: 'SYSTEM', label: 'Closing completed support tasks' },
    REMOVE_ENTER_RESPONSIBILITY_TAG: { preserve: true, owner: 'SYSTEM', label: 'Clearing internal ownership flag' },
    MESSAGE_PROVIDER: { preserve: true, owner: 'PROVIDER', label: 'Provider information requested' },
    WAITING_FOR_PROVIDER: {
        preserve: true,
        owner: 'PROVIDER',
        visual: 'PROVIDER_ACTION_NEEDED',
        label: 'Waiting for provider',
    },
    WAITING_FOR_SUPPORT: {
        preserve: true,
        owner: 'ENTER',
        visual: 'ENTER_ACTION_NEEDED',
        label: 'Waiting for Enter review',
    },
    ESCALATE_TO_HUMAN: {
        preserve: true,
        owner: 'ENTER',
        visual: 'ENTER_ACTION_NEEDED',
        label: 'Escalated for Enter review',
    },
    WAITING_FOR_EXPERIMENT: {
        preserve: true,
        owner: 'SYSTEM',
        visual: 'SYSTEM_BLOCKED',
        label: 'Waiting for experiment',
    },
    WAITING_FOR_FUTURE_DATE: {
        stage: 'PRIMARY_COVERAGE',
        phase: 'SUBMISSION_READINESS',
        owner: 'SYSTEM',
        visual: 'SCHEDULED',
        label: 'Waiting until service date',
    },
    WAITING_ON_GENERATED_PAYER: {
        phase: 'SUBMISSION_READINESS',
        owner: 'ENTER',
        visual: 'ENTER_ACTION_NEEDED',
        label: 'Generated payer needs mapping',
    },
    WAITING_ON_GENERATED_FACILITY: {
        phase: 'SUBMISSION_READINESS',
        owner: 'ENTER',
        visual: 'ENTER_ACTION_NEEDED',
        label: 'Generated facility needs mapping',
    },
    WAITING_ON_CHARGEMASTER: {
        phase: 'SUBMISSION_READINESS',
        owner: 'ENTER',
        visual: 'ENTER_ACTION_NEEDED',
        label: 'Charge configuration needs review',
    },
    WAITING_PAYER_HALT: { phase: 'SUBMISSION_READINESS', label: 'Coverage billing hold' },
    WAITING_CLAIM_HALT: { preserve: true, label: 'Claim paused' },
    SUBMIT_CLAIM: { owner: 'SYSTEM', label: 'Submitting original claim' },
    SUBMIT_CLAIM_REPLACEMENT: {
        phase: 'CLAIM_CORRECTION',
        owner: 'SYSTEM',
        label: 'Submitting replacement claim',
        rework: 'Replacement claim submitted',
    },
    WAITING_FOR_PAYERS: {
        phase: 'PAYER_REVIEW',
        owner: 'PAYER',
        visual: 'AWAITING_PAYER',
        label: 'Waiting for payer response',
    },
    WAITING_FOR_ENROLLMENT: {
        stage: 'PRIMARY_COVERAGE',
        phase: 'REJECTION_FOLLOW_UP',
        owner: 'SYSTEM',
        visual: 'SCHEDULED',
        label: 'Waiting for payer enrollment',
    },
    ADD_MODIFIER_CODE: {
        stage: 'PRIMARY_COVERAGE',
        phase: 'DENIAL_FOLLOW_UP',
        owner: 'SYSTEM',
        label: 'Correcting claim modifiers',
        rework: 'Modifiers corrected after denial',
    },
    SEND_MEDICAL_RECORDS: {
        stage: 'PRIMARY_COVERAGE',
        phase: 'DENIAL_FOLLOW_UP',
        owner: 'SYSTEM',
        label: 'Sending documentation response',
        rework: 'Documentation sent after denial',
    },
    CREATE_RECONSIDERATION: {
        stage: 'PRIMARY_COVERAGE',
        phase: 'DENIAL_FOLLOW_UP',
        owner: 'SYSTEM',
        label: 'Creating reconsideration',
        rework: 'Reconsideration after denial',
    },
    CREATE_APPEAL: {
        stage: 'PRIMARY_COVERAGE',
        phase: 'DENIAL_FOLLOW_UP',
        owner: 'SYSTEM',
        label: 'Creating appeal',
        rework: 'Appeal after denial',
    },
    SWITCH_TO_PRIVATE_PAY: { owner: 'SYSTEM', label: 'Moving claim to private pay' },
    ENABLE_GUARANTOR_COLLECTIONS: {
        stage: 'PATIENT_BALANCE',
        phase: 'PATIENT_BILLING',
        owner: 'SYSTEM',
        label: 'Starting patient collections',
    },
    CLOSE_CLAIM: { owner: 'SYSTEM', label: 'Closing claim' },
    WAITING_FOR_ACTION_CONFIGURATION: {
        preserve: true,
        owner: 'ENTER',
        visual: 'ENTER_ACTION_NEEDED',
        label: 'Follow-up action needs configuration',
    },
};

interface IActionRule {
    visual: VisualState;
    label: string;
    detail: string;
    owner?: EventOwner;
    phase?: OperationalPhase;
    stage?: JourneyStage;
    evidence?: EvidenceLevel;
    rework?: string;
    /** Coding-style rework reopens an earlier stage while the cursor stays where the claim is. */
    reworkStage?: JourneyStage;
    reworkPhase?: OperationalPhase;
    preserve?: boolean;
    /** The send-request actions compose the payer level into the label. */
    composePayer?: (level: PayerLevel) => string;
}

const payerWord = (level: PayerLevel): string =>
    level === 'PRIMARY' ? 'Primary' : level === 'SECONDARY' ? 'Secondary' : level === 'TERTIARY' ? 'Tertiary' : '';

const withPayer = (suffix: string) => (level: PayerLevel) => {
    const word = payerWord(level);
    return word ? `${word} ${suffix}` : `${suffix[0].toUpperCase()}${suffix.slice(1)}`;
};

/** Spec "Action outcome mapping". Global Action rules take precedence over the Status baseline. */
const ACTION_RULES: Record<string, IActionRule> = {
    ENTER_CODER_CREATED: {
        visual: 'COMPLETED',
        label: 'Coding request created',
        detail: 'Coding request accepted; coding itself is not yet complete.',
        owner: 'SYSTEM',
    },
    VENDOR_CODER_CREATED: {
        visual: 'COMPLETED',
        label: 'Coding request created',
        detail: 'Vendor coding request accepted; coding itself is not yet complete.',
        owner: 'SYSTEM',
    },
    THIRD_PARTY_CODER_CREATED: {
        visual: 'ACTIVE',
        label: 'Third-party coding requested for claim correction',
        detail: 'Coding request accepted; coding itself is not yet complete.',
        owner: 'SYSTEM',
        rework: 'Coding requested for claim correction',
        reworkStage: 'CLAIM_PREPARATION',
        reworkPhase: 'CODING',
    },
    ENTER_CODER_RAN: {
        visual: 'COMPLETED',
        label: 'Coding completed',
        detail: 'Enter coding ran; a billable professional claim is still required before moving on.',
        owner: 'SYSTEM',
    },
    WAITING_FOR_PROFESSIONAL_CREATED: {
        visual: 'ACTIVE',
        label: 'Creating billable claim',
        detail: 'Waiting for the billable professional claim to be created.',
        owner: 'SYSTEM',
    },
    CLAIM_SPLIT: {
        visual: 'COMPLETED',
        label: 'Claim split into payer-specific claims',
        detail: 'Branch event; the split is not a backward step.',
        owner: 'SYSTEM',
    },
    STORED_SFTP_DOCUMENTS_TO_CLAIM: {
        visual: 'COMPLETED',
        label: 'Claim documents stored',
        detail: 'A document import was recorded for this claim; the tuple does not prove every document arrived.',
        owner: 'SYSTEM',
    },
    STORED_MESSAGE_DOCUMENTS_TO_CLAIM: {
        visual: 'COMPLETED',
        label: 'Documents from provider task attached',
        detail: 'Documents were linked from a task comment.',
        owner: 'SYSTEM',
    },
    STORED_AUTHORIZATION: {
        visual: 'COMPLETED',
        label: 'Primary authorization added',
        detail: 'Authorization requirement complete; readiness was re-evaluated.',
        owner: 'SYSTEM',
    },
    ELIGIBILITY_PASSED: {
        visual: 'COMPLETED',
        label: 'Eligibility check completed',
        detail: 'The worker completed the eligibility call; the returned response is not exposed on this event.',
        owner: 'SYSTEM',
        evidence: 'CLAIM_CONTEXT_REQUIRED',
    },
    RAN_ELIGIBILITY_CHECK: {
        visual: 'COMPLETED',
        label: 'Eligibility check ran',
        detail: 'The eligibility request ran; it does not prove the response passed analysis.',
        owner: 'SYSTEM',
        evidence: 'CLAIM_CONTEXT_REQUIRED',
    },
    ELIGIBILITY_OVERRIDDEN: {
        visual: 'COMPLETED',
        label: 'Eligibility requirement overridden',
        detail: 'Coverage verification was bypassed by an explicit override.',
        owner: 'SYSTEM',
    },
    UPDATED_PRIMARY_PAYER: {
        visual: 'COMPLETED',
        label: 'Primary coverage updated',
        detail: 'The primary payer changed and eligibility will be checked again.',
        owner: 'SYSTEM',
        rework: 'Primary payer changed',
    },
    UPDATED_PRIMARY_SUBSCRIBER: {
        visual: 'COMPLETED',
        label: 'Primary coverage updated',
        detail: 'The primary subscriber changed and eligibility will be checked again.',
        owner: 'SYSTEM',
        rework: 'Primary subscriber changed',
    },
    REVERTED_PAYER_SWAP: {
        visual: 'COMPLETED',
        label: 'Payer change reverted',
        detail: 'A payer change was rolled back.',
        owner: 'SYSTEM',
        rework: 'Payer change reverted',
    },
    SET_CLAIM_TO_SELF_PAY: {
        visual: 'COMPLETED',
        label: 'Claim moved to patient billing',
        detail: 'The claim now routes to the patient. Collections are not thereby enabled or complete.',
        owner: 'SYSTEM',
        stage: 'PATIENT_BALANCE',
        phase: 'PATIENT_BILLING',
    },
    SENT_CLAIM_SEND_REQUEST: {
        visual: 'AWAITING_TRANSMISSION_STATUS',
        label: 'Claim send requested',
        detail: 'The API accepted the send request; clearinghouse acknowledgement and payer acceptance are not yet known.',
        owner: 'SYSTEM',
        phase: 'TRANSMISSION_STATUS',
        composePayer: withPayer('claim send requested'),
    },
    SENT_CLAIM_SEND_REPLACEMENT_REQUEST: {
        visual: 'AWAITING_TRANSMISSION_STATUS',
        label: 'Replacement claim send requested',
        detail: 'The API accepted the replacement send request; transmission status is not yet known.',
        owner: 'SYSTEM',
        phase: 'TRANSMISSION_STATUS',
        rework: 'Replacement claim sent',
        composePayer: withPayer('replacement claim send requested'),
    },
    WAITING_FOR_PAYERS: {
        visual: 'AWAITING_PAYER',
        label: 'Waiting for payer',
        detail: 'A payer response is pending.',
        owner: 'PAYER',
        phase: 'PAYER_REVIEW',
    },
    SUBMITTED_DOCUMENTATION_TO_PRIMARY: {
        visual: 'AWAITING_PAYER',
        label: 'Documentation sent to primary payer',
        detail: 'A documentation response was sent. Awaiting payer review of the submission.',
        owner: 'PAYER',
    },
    CREATED_APPEAL_DOCUMENT: {
        visual: 'ACTIVE',
        label: 'Appeal packet ready for review',
        detail: 'The follow-up packet was created but not sent.',
        owner: 'SYSTEM',
    },
    SUBMITTED_RECONSIDERATION: {
        visual: 'AWAITING_PAYER',
        label: 'Reconsideration sent to payer',
        detail: 'The follow-up send call succeeded; awaiting payer review.',
        owner: 'PAYER',
    },
    ADDED_MODIFIERS_TO_SERVICE_LINES: {
        visual: 'COMPLETED',
        label: 'Claim modifiers corrected',
        detail: 'Correction completed; denial follow-up stays active until the claim is resubmitted.',
        owner: 'SYSTEM',
    },
    SENT_PROVIDER_MESSAGE: {
        visual: 'PROVIDER_ACTION_NEEDED',
        label: 'Provider message sent',
        detail: 'A provider-facing task or comment was created or updated.',
        owner: 'PROVIDER',
    },
    REOPEN_PROVIDER_MESSAGE: {
        visual: 'PROVIDER_ACTION_NEEDED',
        label: 'Provider task reopened',
        detail: 'A provider-facing task was reopened.',
        owner: 'PROVIDER',
    },
    REOPENED_MESSAGE: {
        // The owner depends on the reopened task, so the state must not claim a provider action.
        visual: 'ACTIVE',
        label: 'Follow-up task reopened',
        detail: 'A follow-up task was reopened; the owner depends on the task.',
        owner: 'UNKNOWN',
        evidence: 'CLAIM_CONTEXT_REQUIRED',
    },
    SENT_SUPPORT_MESSAGE: {
        visual: 'ENTER_ACTION_NEEDED',
        label: 'Sent to Enter for review',
        detail: 'A support-facing task or comment was opened or updated.',
        owner: 'ENTER',
    },
    TAGGED_FOR_SUPPORT_REVIEW: {
        visual: 'ENTER_ACTION_NEEDED',
        label: 'Flagged for Enter review',
        detail: 'An Enter review tag was applied; a support task may not exist.',
        owner: 'ENTER',
    },
    ESCALATE_TO_HUMAN: {
        visual: 'ENTER_ACTION_NEEDED',
        label: 'Escalated for Enter review',
        detail: 'Enter review is required; a support task may not exist.',
        owner: 'ENTER',
    },
    CLOSED_MESSAGES_IN_CATEGORY: {
        visual: 'COMPLETED',
        label: 'Follow-up task closed',
        detail: 'Cleanup event; the claim is being re-evaluated.',
        owner: 'SYSTEM',
        preserve: true,
    },
    CLOSED_MESSAGES_TO_SUPPORT_IN_CATEGORY: {
        visual: 'COMPLETED',
        label: 'Follow-up task closed',
        detail: 'Cleanup event; the claim is being re-evaluated.',
        owner: 'SYSTEM',
        preserve: true,
    },
    REMOVED_ENTER_RESPONSIBILITY_TAG: {
        visual: 'COMPLETED',
        label: 'Ownership cleared',
        detail: 'Cleanup event; the claim is being re-evaluated.',
        owner: 'SYSTEM',
        preserve: true,
    },
    INVALIDATED_CLAIM: {
        visual: 'PROVIDER_ACTION_NEEDED',
        label: 'Claim returned to EMR for correction',
        detail: 'The source EMR claim was invalidated for provider correction.',
        owner: 'PROVIDER',
        rework: 'Claim invalidated for correction',
    },
    WAITING: {
        visual: 'ACTIVE',
        label: 'Waiting',
        detail: 'Waiting on a named prerequisite.',
        owner: 'UNKNOWN',
    },
    WAITING_FOR_SUPPORT: {
        visual: 'ENTER_ACTION_NEEDED',
        label: 'Waiting for Enter review',
        detail: 'Automation stopped for Enter review; a support task may not exist.',
        owner: 'ENTER',
    },
    WAITING_FOR_EXPERIMENTS: {
        visual: 'SYSTEM_BLOCKED',
        label: 'Waiting for experiment to finish',
        detail: 'An experiment is blocking this work.',
        owner: 'SYSTEM',
    },
    WAITING_FOR_FUTURE_DATE: {
        visual: 'SCHEDULED',
        label: 'Waiting until service date',
        detail: 'The date of service has not arrived in the clearinghouse timezone.',
        owner: 'SYSTEM',
    },
    WAITING_FOR_VALID_HL7: {
        visual: 'PROVIDER_ACTION_NEEDED',
        label: 'Waiting for valid EMR claim data',
        detail: 'Coding is blocked until a valid HL7 message arrives from the client EMR.',
        owner: 'PROVIDER',
    },
    WAITING_FOR_EMR: {
        visual: 'PROVIDER_ACTION_NEEDED',
        label: 'Waiting for EMR update',
        detail: 'Waiting for an update from the client EMR.',
        owner: 'PROVIDER',
    },
    WAITING_FOR_ENROLLMENT: {
        visual: 'SCHEDULED',
        label: 'Waiting for payer enrollment',
        detail: 'The claim cannot be resubmitted until enrollment is active; retry window pending.',
        owner: 'SYSTEM',
    },
    WAITING_ON_GENERATED_PAYER: {
        visual: 'ENTER_ACTION_NEEDED',
        label: 'Generated payer needs mapping',
        detail: 'A generated payer must be mapped before submission.',
        owner: 'ENTER',
    },
    WAITING_ON_GENERATED_FACILITY: {
        visual: 'ENTER_ACTION_NEEDED',
        label: 'Generated facility needs mapping',
        detail: 'A generated facility must be mapped before submission.',
        owner: 'ENTER',
    },
    WAITING_ON_CHARGEMASTER: {
        visual: 'ENTER_ACTION_NEEDED',
        label: 'Charge configuration needs review',
        detail: 'Missing or inactive charge configuration blocks submission until it is repaired.',
        owner: 'ENTER',
    },
    WAITING_PAYER_HALT: {
        visual: 'UNCLASSIFIED',
        label: 'Coverage billing hold',
        detail: 'A payer or provider billing hold is active; the owner is not identified by this event.',
        owner: 'UNKNOWN',
    },
    WAITING_CLAIM_HALT: {
        visual: 'UNCLASSIFIED',
        label: 'Claim paused',
        detail: 'The claim is halted.',
        owner: 'UNKNOWN',
    },
    ENABLED_COLLECTIONS: {
        // Enabling collections is not completing them: the balance stays active.
        visual: 'ACTIVE',
        label: 'Patient collections enabled',
        detail: 'Collection enablement succeeded. The balance remains active until settled or written off.',
        owner: 'GUARANTOR',
        stage: 'PATIENT_BALANCE',
        phase: 'PATIENT_BILLING',
    },
    WORKER_STEP_NOT_IMPLEMENTED: {
        visual: 'SYSTEM_BLOCKED',
        label: 'Automation not implemented for this step',
        detail: 'Automation is not implemented for this step; this is not progress.',
        owner: 'ENTER',
    },
    USER_NOT_SETUP_FOR_WORKER: {
        visual: 'SYSTEM_BLOCKED',
        label: 'Automation unavailable for this account',
        detail: 'Claim automation is not set up for the account; this is not a claim pause.',
        owner: 'ENTER',
        preserve: true,
    },
};

/** Statuses placed in Primary Coverage by default whose payer level genuinely needs claim context. */
const NEEDS_PAYER_CONTEXT = new Set(['WAITING_FOR_PAYMENT', 'REJECTED_CLAIM', 'DENIED_CLAIM']);

/** Claim splitting branches a claim into children; it is not a step back (any action: split, or its error). */
const isClaimSplit = (status: string, step: string, action: string): boolean =>
    status === 'CLAIM_SPLIT' ||
    status === 'HIGH_VALUE_CLAIM_SPLIT' ||
    step === 'SPLIT_CLAIM' ||
    action === 'CLAIM_SPLIT';

/** Actions handled by the global rules below rather than by `ACTION_RULES`. */
const NON_OUTCOME_ACTIONS = new Set(['ERROR', 'NOOP', 'UNKNOWN', 'CLOSED_CLAIM']);

/** Operation named by the failed step, for "Could not …" copy. */
const ERROR_OPERATION: Record<string, string> = {
    SUBMIT_CLAIM: 'submit claim',
    SUBMIT_CLAIM_REPLACEMENT: 'submit replacement claim',
    RUN_ELIGIBILITY_CHECK: 'run eligibility check',
    CLOSE_CLAIM: 'close claim',
    ENABLE_GUARANTOR_COLLECTIONS: 'enable patient collections',
    SEND_MEDICAL_RECORDS: 'send documentation',
    CREATE_APPEAL: 'create appeal',
    CREATE_RECONSIDERATION: 'create reconsideration',
    ADD_MODIFIER_CODE: 'correct modifiers',
    GET_MISSING_INFORMATION: 'gather missing information',
    SPLIT_CLAIM: 'split claim',
    RUN_CODING: 'run coding',
};

const norm = (value?: string): string => (value || '').trim().toUpperCase();

export interface IClaimProgressEvent {
    key: string;
    date: string;
    /** Raw tuple, verbatim, for developer details. */
    status: string;
    step: string;
    action: string;
    stage: JourneyStage;
    phase: OperationalPhase;
    visualState: VisualState;
    overlays: EventOverlay[];
    owner: EventOwner;
    payerLevel: PayerLevel;
    label: string;
    detail: string;
    evidence: EvidenceLevel;
    isRework: boolean;
    reworkReason?: string;
    /** Number of consecutive identical wait events folded into this row (1 = not grouped). */
    repeatCount: number;
    /** Stage/phase could not be read off the tuple and were taken from an older event. */
    stageFromHistory: boolean;
    /** Payer the claim is being billed to, when this row is a wait on a prior payer (primary → secondary). */
    targetPayerLevel?: PayerLevel;
    /** The stage that becomes active next (shown as "Next" on its node). */
    nextStage?: JourneyStage;
    /** An earlier stage reopened by rework while the cursor stays elsewhere (e.g. coding during a correction). */
    reworkStage?: JourneyStage;
    reworkPhase?: OperationalPhase;
    /** Consecutive-error attempt number, for ERROR rows. */
    attempt?: number;
    /** Why the claim is closed (closeout description / voided), shown under the Complete node. */
    terminalReason?: string;
}

interface IDerivedCore extends Omit<IClaimProgressEvent, 'key' | 'date' | 'repeatCount' | 'stageFromHistory'> {
    preserve: boolean;
}

const WEAKER: Record<EvidenceLevel, number> = {
    TUPLE_PROVEN: 0,
    HISTORY_DERIVED: 1,
    CLAIM_CONTEXT_REQUIRED: 2,
    UNAVAILABLE: 3,
};

const weakest = (a: EvidenceLevel, b: EvidenceLevel): EvidenceLevel => (WEAKER[a] >= WEAKER[b] ? a : b);

/** Fallback copy when the tuple is outside the spec: use the API's own labels, never invented prose. */
const apiLabel = (item: ClaimWorkflowHistoryItem): string =>
    item.actionLabel || item.workflowStepLabel || item.workflowStatusLabel || 'Workflow event';

const apiDetail = (item: ClaimWorkflowHistoryItem): string =>
    item.actionDescription || item.workflowStepDescription || item.workflowStatusDescription || '';

/**
 * Pure per-row mapping. Precedence (spec): compound/global Action rules → Status baseline →
 * Step override → Action outcome. History-dependent facts (preserved stage, error streak, rework
 * after payer activity) are filled in by `deriveClaimProgress`.
 */
const deriveCore = (item: ClaimWorkflowHistoryItem, errorStreak: number): IDerivedCore => {
    const status = norm(item.workflowStatus);
    const step = norm(item.workflowStep);
    const action = norm(item.action);

    const baseline = STATUS_BASELINE[status];
    const stepRule = STEP_RULES[step];
    const actionRule = ACTION_RULES[action];

    let stage: JourneyStage = baseline?.stage ?? 'UNCLASSIFIED';
    let phase: OperationalPhase = baseline?.phase ?? 'UNCLASSIFIED';
    let payerLevel: PayerLevel = baseline?.payer ?? 'UNKNOWN';
    let owner: EventOwner = 'UNKNOWN';
    let visualState: VisualState = 'UNCLASSIFIED';
    // A status this code has never seen (the backend added one): not "no workflow state" (UNKNOWN) and not a
    // halt. Keep the claim on its last known stage, and say what the API says about the new status.
    const isNewStatus = Boolean(status) && !baseline && status !== 'HALTED' && status !== 'UNKNOWN';
    let label = baseline?.label ?? (isNewStatus ? item.workflowStatusLabel || apiLabel(item) : apiLabel(item));
    let detail = isNewStatus ? item.workflowStatusDescription || apiDetail(item) : apiDetail(item);
    // A halt status proves the pause itself; which stage it sits on is filled in from history.
    let evidence: EvidenceLevel = baseline || status === 'HALTED' ? 'TUPLE_PROVEN' : 'UNAVAILABLE';
    let reworkReason: string | undefined;
    // Only a halt (or a step that says so) borrows the last meaningful stage. A bare UNKNOWN status
    // stays unclassified: it is "no supported work item", not "same place as before".
    let preserve = status === 'HALTED' || isNewStatus;
    let targetPayerLevel: PayerLevel | undefined;
    let nextStage: JourneyStage | undefined;
    let reworkStage: JourneyStage | undefined;
    let reworkPhase: OperationalPhase | undefined;
    const overlays: EventOverlay[] = [];

    // A bare Status baseline whose coverage level needs payer context cannot be proven from the tuple.
    if (
        baseline &&
        (baseline.payer === 'UNKNOWN' || baseline.stage === 'UNCLASSIFIED' || NEEDS_PAYER_CONTEXT.has(status))
    ) {
        evidence = 'CLAIM_CONTEXT_REQUIRED';
    }

    // Step override
    if (stepRule) {
        if (stepRule.stage) stage = stepRule.stage;
        if (stepRule.phase) phase = stepRule.phase;
        if (stepRule.owner) owner = stepRule.owner;
        if (stepRule.visual) visualState = stepRule.visual;
        if (stepRule.label) label = stepRule.label;
        if (stepRule.rework) reworkReason = stepRule.rework;
        if (stepRule.preserve && !baseline) preserve = true;
        if (stepRule.preserve && baseline && stage === 'UNCLASSIFIED') preserve = true;
    }

    // Payer level implied by the Step alone (e.g. UNKNOWN / WAITING_FOR_PAYERS) needs context.
    if (step === 'WAITING_FOR_PAYERS' && payerLevel === 'NONE') {
        payerLevel = 'UNKNOWN';
        evidence = weakest(evidence, 'CLAIM_CONTEXT_REQUIRED');
    }

    // A generic WAITING action names no owner; the Step does ("never use a generic owner").
    const stepNamesTheWait = action === 'WAITING' && Boolean(stepRule?.visual);

    // Action outcome
    if (actionRule && stepNamesTheWait) {
        detail = 'Waiting on the prerequisite named by this step; whether a task is still open needs claim context.';
        evidence = weakest(evidence, 'CLAIM_CONTEXT_REQUIRED');
    } else if (actionRule) {
        visualState = actionRule.visual;
        label = actionRule.composePayer ? actionRule.composePayer(payerLevel) : actionRule.label;
        detail = actionRule.detail;
        if (actionRule.owner && actionRule.owner !== 'UNKNOWN') owner = actionRule.owner;
        if (actionRule.phase) phase = actionRule.phase;
        if (actionRule.stage) {
            stage = actionRule.stage;
            payerLevel = 'NONE';
        }
        if (actionRule.rework) reworkReason = reworkReason ?? actionRule.rework;
        if (actionRule.reworkStage) {
            reworkStage = actionRule.reworkStage;
            reworkPhase = actionRule.reworkPhase;
        }
        if (actionRule.preserve) preserve = true;
        if (actionRule.evidence) evidence = weakest(evidence, actionRule.evidence);
    } else if (action && !NON_OUTCOME_ACTIONS.has(action)) {
        // Unknown Action: keep the API wording, say nothing stronger than "recorded".
        // A new status already carries its own label and description; they stay the headline.
        if (!isNewStatus) {
            label = stepRule?.label ?? baseline?.label ?? apiLabel(item);
            detail = apiDetail(item);
        }
        evidence = weakest(evidence, 'UNAVAILABLE');
    }

    // WAITING_FOR_PAYERS compound rules (Status decides the stage; Step decides the phase).
    if (step === 'WAITING_FOR_PAYERS' || action === 'WAITING_FOR_PAYERS') {
        if (status === 'NEED_SECONDARY_TRANSMIT') {
            stage = 'PRIMARY_COVERAGE';
            phase = 'PAYER_REVIEW';
            payerLevel = 'PRIMARY';
            targetPayerLevel = 'SECONDARY';
            nextStage = 'ADDITIONAL_COVERAGE';
            label = 'Waiting for primary payer';
            detail = 'Primary adjudication is required before the claim can be billed to secondary coverage.';
        } else if (status === 'NEED_TERTIARY_TRANSMIT') {
            stage = 'ADDITIONAL_COVERAGE';
            phase = 'PAYER_REVIEW';
            payerLevel = 'UNKNOWN';
            label = 'Waiting for prior payer';
            detail =
                'Primary or secondary payment information is needed before tertiary billing; the awaited payer needs claim context.';
            evidence = weakest(evidence, 'CLAIM_CONTEXT_REQUIRED');
        } else if (status === 'PROVIDER_DENIAL_WORKER') {
            phase = 'DENIAL_FOLLOW_UP';
            label = 'Payer reviewing follow-up';
            detail = 'An appeal or reconsideration was already sent.';
        } else if (status === 'NEED_PRIMARY_RETRANSMIT') {
            phase = 'PAYER_REVIEW';
            label = 'Correction pending payer response';
            detail = 'The claim changed after a primary send; a payer response is pending.';
        } else if (status === 'WAITING_FOR_PAYMENT') {
            // Which payer is awaited is not in the tuple; history or the claim's payer count resolves it.
            phase = 'PAYER_REVIEW';
            label = 'Waiting for payer';
            detail = 'A payer response is pending. The awaited payer needs claim context.';
            evidence = weakest(evidence, 'CLAIM_CONTEXT_REQUIRED');
        } else if (status === 'UNKNOWN') {
            phase = 'PAYER_REVIEW';
            payerLevel = 'UNKNOWN';
            label = 'Payer response pending — payer unknown';
            detail = 'A payer response is pending; which payer needs claim context.';
            evidence = weakest(evidence, 'CLAIM_CONTEXT_REQUIRED');
        }
    }

    // The same Action means different things by Step/Status (spec caveats 4 and 5).
    if (action === 'SUBMITTED_RECONSIDERATION' && step === 'CREATE_APPEAL') {
        label = 'Appeal sent to payer';
    }
    if (action === 'STORED_SFTP_DOCUMENTS_TO_CLAIM') {
        if (status === 'NEED_MEDICAL_RECORDS') {
            label = 'Medical records attached';
            detail = 'Records were stored on the claim; readiness will be re-evaluated.';
        } else if (status === 'NEED_CLAIM_DOCUMENTS') {
            label = 'Claim document import attempted';
            detail =
                'An import was attempted; the worker records this action even when the import fails, so it does not prove the documents arrived.';
        }
    }

    if (action === 'THIRD_PARTY_CODER_CREATED' && status === 'NEED_PRIMARY_RETRANSMIT') {
        reworkReason = 'A newer professional differs from the transmitted claim, so coding is running again';
        evidence = weakest(evidence, 'HISTORY_DERIVED');
    }

    // A rejection or denial parked for Enter keeps its own headline, not the generic "Waiting for Enter review".
    if (
        (status === 'REJECTED_CLAIM' || status === 'DENIED_CLAIM') &&
        (step === 'WAITING_FOR_SUPPORT' || action === 'WAITING_FOR_SUPPORT')
    ) {
        label = baseline?.label ?? label;
        detail = `Automation stopped for review. Payer level and ${status === 'REJECTED_CLAIM' ? 'rejection' : 'denial'} reason require ${status === 'REJECTED_CLAIM' ? 'transmission' : 'payment or transmission'} context.`;
    }

    if (label === 'Waiting for payer' && payerWord(payerLevel)) {
        label = `Waiting for ${payerWord(payerLevel).toLowerCase()} payer`;
    }

    // Halt: an overlay on the last meaningful stage, never a stage of its own.
    if (status === 'HALTED' || step === 'WAITING_CLAIM_HALT' || step === 'WAITING_PAYER_HALT') {
        overlays.push('PAUSED');
        const claimHalt = status === 'HALTED' || step === 'WAITING_CLAIM_HALT';
        const haltAction = action === 'WAITING' || action === 'SENT_PROVIDER_MESSAGE';
        if (action !== 'ERROR' && claimHalt && haltAction) {
            label = status === 'HALTED' ? 'Claim paused' : label;
            // Normally the provider, who is asked to act on the halt.
            visualState = 'PROVIDER_ACTION_NEEDED';
            owner = 'PROVIDER';
            detail =
                action === 'SENT_PROVIDER_MESSAGE'
                    ? 'The halt conversation was opened with the provider.'
                    : 'A claim-level halt is in effect. The underlying stage is preserved until the halt is removed.';
        } else if (action !== 'ERROR' && !claimHalt) {
            detail = detail || 'The claim is halted.';
        }
    }

    // Global Actions that override a misleading Status baseline.
    if (action === 'CLOSED_CLAIM') {
        stage = 'CLOSURE';
        phase = 'CLOSURE';
        payerLevel = 'TERMINAL';
        visualState = 'COMPLETED';
        owner = 'NONE';
        label = 'Claim closed';
        detail =
            'Administrative closure is recorded. CLOSED_CLAIM alone never means paid, so no financial outcome is stated.';
        evidence = 'TUPLE_PROVEN';
        preserve = false;
        overlays.length = 0;
    } else if (action === 'NOOP') {
        // The Step may still name a wait; the Action wins and nothing is claimed as passed.
        visualState = 'BYPASSED';
        owner = 'SYSTEM';
        label = 'Automation bypassed for this requirement';
        detail = 'The worker did not perform the requested operation and tagged the status for bypass.';
        if (baseline?.stage === 'UNCLASSIFIED') evidence = weakest(evidence, 'CLAIM_CONTEXT_REQUIRED');
    } else if (action === 'ERROR') {
        const operation = ERROR_OPERATION[step];
        const level = payerWord(payerLevel).toLowerCase();
        // "Could not submit primary claim": the payer level goes in front of the word "claim".
        label = operation
            ? `Could not ${level ? operation.replace(/\bclaim\b/, `${level} claim`) : operation}`
            : 'Workflow step failed';
        if (step === 'CLOSE_CLAIM') label = 'Claim closure failed';
        detail =
            step === 'CLOSE_CLAIM'
                ? 'A closure attempt errored. Closure remains active or blocked and must not read as complete.'
                : errorStreak >= 3
                  ? 'Three consecutive errors on the same status and step. Automation will not retry without a forced run.'
                  : `Attempt ${Math.max(errorStreak, 1)} failed; the worker can retry. The error text is not exposed on this event.`;
        if (errorStreak >= 3) {
            visualState = 'ACTION_REQUIRED_AFTER_RETRIES';
            owner = 'ENTER';
        } else {
            visualState = 'RETRYING';
            owner = 'SYSTEM';
        }
        evidence = weakest(evidence, 'HISTORY_DERIVED');
    } else if (action === 'UNKNOWN' && !stepRule?.visual) {
        // Never read as Paused and never defaulted to a stage: the tuple supports nothing.
        visualState = 'UNCLASSIFIED';
        if (!baseline) {
            label = 'Current workflow state is unavailable';
            detail = 'No reliable stage, phase, or owner can be derived. Prior events are shown as history only.';
            evidence = 'UNAVAILABLE';
        }
    }

    // Closure attempt that did not succeed must not read as complete.
    if (status === 'NEEDS_CLOSED' && action !== 'CLOSED_CLAIM' && visualState === 'UNCLASSIFIED') {
        visualState = 'ACTIVE';
        owner = 'SYSTEM';
    }

    // Visual fallbacks: a classified stage with no explicit outcome is simply being worked.
    if (visualState === 'UNCLASSIFIED' && !overlays.includes('PAUSED') && stage !== 'UNCLASSIFIED') {
        visualState = 'ACTIVE';
        if (owner === 'UNKNOWN') owner = 'SYSTEM';
    }

    return {
        status,
        step,
        action,
        stage,
        phase,
        visualState,
        overlays,
        owner,
        payerLevel,
        label,
        detail,
        evidence,
        isRework: Boolean(reworkReason),
        reworkReason,
        targetPayerLevel,
        nextStage,
        reworkStage,
        reworkPhase,
        attempt: action === 'ERROR' ? Math.max(errorStreak, 1) : undefined,
        preserve,
    };
};

/**
 * Only WAITING* actions are heartbeat-like waits. Two identical sends, reconsiderations or
 * support tags are two events, so they are never folded together.
 */
const isWaitEvent = (event: IClaimProgressEvent): boolean => event.action.startsWith('WAITING');

const stageOrder = (stage: JourneyStage): number => JOURNEY_STAGES.indexOf(stage);

const toTime = (value?: string): number => {
    if (!value) return 0;
    const ts = new Date(value).getTime();
    return Number.isFinite(ts) ? ts : 0;
};

const isSendAction = (action: string): boolean =>
    action === 'SENT_CLAIM_SEND_REQUEST' || action === 'SENT_CLAIM_SEND_REPLACEMENT_REQUEST';

/**
 * What the oldest-first pass has seen so far. Every "was there an earlier X" question the loop asks is
 * answered from here in O(1), instead of rescanning the events derived so far for each new row.
 */
interface IHistoryState {
    /** Last event whose stage could be classified (the anchor for halts and cleanup rows). */
    lastMeaningful?: IClaimProgressEvent;
    lastAdditional?: IClaimProgressEvent;
    /** Last event in Primary or Additional Coverage. */
    lastCoverage?: IClaimProgressEvent;
    hadSend: boolean;
    /** Highest journey-stage index reached so far (-1 = none). */
    highestStage: number;
    /** Payer levels a claim has already been sent to. */
    sentLevels: Set<PayerLevel>;
    /** The current run of consecutive ERROR rows on one status + step. */
    errorRun: { status: string; step: string; count: number };
}

const newHistoryState = (): IHistoryState => ({
    hadSend: false,
    highestStage: -1,
    sentLevels: new Set<PayerLevel>(),
    errorRun: { status: '', step: '', count: 0 },
});

/** Consecutive matching ERROR rows ending at this one (0 when this row is not an ERROR). */
const nextErrorStreak = (seen: IHistoryState, status: string, step: string, action: string): number => {
    if (action !== 'ERROR') {
        seen.errorRun = { status: '', step: '', count: 0 };
        return 0;
    }
    const run = seen.errorRun;
    const count = run.count > 0 && run.status === status && run.step === step ? run.count + 1 : 1;
    seen.errorRun = { status, step, count };
    return count;
};

const recordEvent = (seen: IHistoryState, event: IClaimProgressEvent): void => {
    if (event.stage !== 'UNCLASSIFIED') seen.lastMeaningful = event;
    if (event.stage === 'ADDITIONAL_COVERAGE') seen.lastAdditional = event;
    if (event.stage === 'PRIMARY_COVERAGE' || event.stage === 'ADDITIONAL_COVERAGE') seen.lastCoverage = event;
    if (isSendAction(event.action)) {
        seen.hadSend = true;
        seen.sentLevels.add(event.payerLevel);
    }
    seen.highestStage = Math.max(seen.highestStage, stageOrder(event.stage));
};

/**
 * Projects raw history (any order) into events, newest first.
 * - Sorts newest first by `actionDate` (stable for equal timestamps: input order is kept).
 * - Resolves "preserve" rows (halt, cleanup, unknown) to the last meaningful older stage.
 * - Derives the consecutive-error streak, rework markers and groups repeated identical waits.
 */
export const deriveClaimProgress = (history: ClaimWorkflowHistoryItem[]): IClaimProgressEvent[] => {
    const sorted = history
        .map((item, index) => ({ item, index }))
        .sort((a, b) => toTime(b.item.actionDate) - toTime(a.item.actionDate) || a.index - b.index)
        .map(entry => entry.item);

    // Oldest-first pass so history context (older events) is available for each row. That context is
    // tracked incrementally below, so the pass stays linear however long the (unpaginated) history is.
    const chronological = [...sorted].reverse();
    const derived: IClaimProgressEvent[] = [];
    const seen = newHistoryState();

    chronological.forEach((item, index) => {
        const status = norm(item.workflowStatus);
        const step = norm(item.workflowStep);

        const errorStreak = nextErrorStreak(seen, status, step, norm(item.action));

        const core = deriveCore(item, errorStreak);
        let stage = core.stage;
        let phase = core.phase;
        let payerLevel = core.payerLevel;
        let evidence = core.evidence;
        let stageFromHistory = false;
        let reworkReason = core.reworkReason;
        let preservedFrom: IClaimProgressEvent | undefined;

        // Preserve semantics: take stage/phase from the last meaningful older event.
        // (The payer-context statuses resolve their stage below, from a coverage stage only.)
        if (
            core.preserve &&
            !NEEDS_PAYER_CONTEXT.has(core.status) &&
            (stage === 'UNCLASSIFIED' || core.overlays.includes('PAUSED'))
        ) {
            const anchor = seen.lastMeaningful;
            if (anchor) {
                preservedFrom = anchor;
                stage = anchor.stage;
                phase = anchor.phase;
                payerLevel = anchor.payerLevel;
                stageFromHistory = true;
                evidence = weakest(evidence, 'HISTORY_DERIVED');
            }
        }

        // WAITING_FOR_PAYMENT waits on the terminal active payer. Once billing has reached additional
        // coverage that is the secondary/tertiary payer, so it stays there rather than "returning" to primary.
        let label = core.label;
        let detail = core.detail;
        if (core.status === 'WAITING_FOR_PAYMENT' && seen.lastAdditional) {
            stage = 'ADDITIONAL_COVERAGE';
            stageFromHistory = true;
            payerLevel =
                seen.lastAdditional.payerLevel === 'SECONDARY' || seen.lastAdditional.payerLevel === 'TERTIARY'
                    ? seen.lastAdditional.payerLevel
                    : 'UNKNOWN';
            const word = payerWord(payerLevel).toLowerCase();
            label = word ? `Waiting for ${word} payer` : 'Waiting for payer';
            detail = 'A payer response is pending. The awaited payer needs claim context.';
            evidence = weakest(evidence, 'HISTORY_DERIVED');
        } else if (NEEDS_PAYER_CONTEXT.has(core.status) && stage === 'UNCLASSIFIED' && seen.lastCoverage) {
            // A rejection / denial / payer wait follows the coverage the claim was last worked in. That is
            // history-derived, not proof — the payer level stays unknown.
            stage = seen.lastCoverage.stage;
            stageFromHistory = true;
            evidence = weakest(evidence, 'HISTORY_DERIVED');
        }

        // Rework after earlier work: retransmit after a successful send, or coding after payer activity.
        if (
            !reworkReason &&
            seen.hadSend &&
            (core.status === 'NEED_PRIMARY_RETRANSMIT' || core.status === 'NEED_PRIMARY_NON_ACKNOWLEDGED_TRANSMIT') &&
            core.action !== 'WAITING_FOR_PAYERS'
        ) {
            reworkReason =
                core.status === 'NEED_PRIMARY_RETRANSMIT'
                    ? 'Claim changed after a primary send'
                    : 'Prior transmission lacked expected status progression';
        }
        if (
            !reworkReason &&
            stage === 'CLAIM_PREPARATION' &&
            phase === 'CODING' &&
            seen.highestStage >= stageOrder('PRIMARY_COVERAGE')
        ) {
            reworkReason = 'Coding resumed after payer activity';
            evidence = weakest(evidence, 'HISTORY_DERIVED');
        }

        // Any move back to an earlier stage is rework, for every stage — not only coding. Forward payer
        // cycles are not (primary → secondary), a split is a branch, and rows that merely hold the last
        // stage (halt, cleanup) carry the rework their anchor already had instead of re-deciding it.
        if (preservedFrom) {
            if (!reworkReason && preservedFrom.isRework) {
                reworkReason = preservedFrom.reworkReason;
            }
        } else if (
            !reworkReason &&
            stage !== 'UNCLASSIFIED' &&
            // A hold (halt) or a split is not movement between stages.
            !core.overlays.includes('PAUSED') &&
            !isClaimSplit(core.status, core.step, core.action)
        ) {
            if (stageOrder(stage) < seen.highestStage) {
                reworkReason = `Returned to ${STAGE_EYEBROW_LABEL[stage]} from ${STAGE_EYEBROW_LABEL[JOURNEY_STAGES[seen.highestStage]]}`;
                evidence = weakest(evidence, 'HISTORY_DERIVED');
            }
        }

        // A claim sent again to the same payer level is a resend, whatever status triggered it.
        const isSend = isSendAction(core.action);
        const levelWord = payerWord(payerLevel);
        if (isSend && levelWord && seen.sentLevels.has(payerLevel)) {
            const stalled = core.status === 'NEED_PRIMARY_NON_ACKNOWLEDGED_TRANSMIT';
            reworkReason =
                reworkReason ??
                (stalled
                    ? 'Prior transmission lacked expected status progression'
                    : 'Claim sent again after an earlier send');
            label = `${levelWord} claim resent`;
            detail = stalled
                ? 'The prior transmission lacked expected status progression; awaiting updated transmission status.'
                : 'The claim was sent again after an earlier send; awaiting updated transmission status.';
            evidence = weakest(evidence, 'HISTORY_DERIVED');
        }

        const overlays = [...core.overlays];
        if (reworkReason) overlays.push('REWORK');

        const event: IClaimProgressEvent = {
            key: `${item.actionDate || 'unknown'}-${status}-${step}-${core.action}-${index}`,
            date: item.actionDate || '',
            status: core.status,
            step: core.step,
            action: core.action,
            stage,
            phase,
            visualState: core.visualState,
            overlays,
            owner: core.owner,
            payerLevel,
            label,
            detail,
            evidence,
            isRework: Boolean(reworkReason),
            reworkReason,
            repeatCount: 1,
            stageFromHistory,
            targetPayerLevel: core.targetPayerLevel,
            nextStage: core.nextStage,
            reworkStage: core.reworkStage,
            reworkPhase: core.reworkPhase,
            attempt: core.attempt,
        };
        derived.push(event);
        recordEvent(seen, event);
    });

    // Back to newest-first, folding adjacent identical waits ("repeated identical waits are grouped").
    const newestFirst = [...derived].reverse();
    const grouped: IClaimProgressEvent[] = [];
    newestFirst.forEach(event => {
        const last = grouped[grouped.length - 1];
        if (
            last &&
            isWaitEvent(event) &&
            last.status === event.status &&
            last.step === event.step &&
            last.action === event.action
        ) {
            last.repeatCount += 1;
            return;
        }
        grouped.push({ ...event });
    });

    return grouped;
};

export interface IClaimProgressContext {
    /**
     * Whether an active secondary or tertiary payer exists on the claim today.
     * `undefined` means unknown — the tracker then makes no "not needed" claim (NS-3).
     */
    hasAdditionalCoverage?: boolean;
    /**
     * The claim's own closeout (`closeoutType` / `closeoutVoid` / `closeoutDescription`). The history has no
     * "closed" status — a closed claim just stops getting meaningful rows, or gets UNKNOWN ones — so a closed
     * or voided claim completes Closure from here, whatever the newest history row says.
     * `undefined` means unknown (not loaded); `closed: false` is an open claim and changes nothing.
     */
    closeout?: IClaimCloseoutContext;
}

/**
 * Whether the claim has an active secondary or tertiary payer, from the medical claim's subscribers.
 * Unknown (`undefined`) unless the medical claims have loaded AND the response actually carries the
 * relation: an omitted `payerSubscribers` is "not told", not "none". `active` is optional too, so an
 * omitted value is an unknown status — neither active nor inactive (NS-3):
 *  - two or more records explicitly `active: true` prove an additional payer: `true`;
 *  - if even counting every unknown as active there could not be two, there is provably none: `false`;
 *  - otherwise a missing status leaves it open: `undefined`.
 */
export const resolveHasAdditionalCoverage = (
    medicalClaimsLoaded: boolean,
    // `object`, not `{ active?: boolean }`: the provider model has no `active` flag at all, so every status is unknown.
    payerSubscribers: ReadonlyArray<object> | undefined,
): boolean | undefined => {
    if (!medicalClaimsLoaded || !Array.isArray(payerSubscribers)) return undefined;
    let proven = 0;
    let unknown = 0;
    for (const subscriber of payerSubscribers) {
        const active = (subscriber as { active?: boolean } | undefined)?.active;
        if (active === true) proven += 1;
        else if (active === undefined) unknown += 1;
    }
    if (proven >= 2) return true;
    if (proven + unknown < 2) return false;
    return undefined;
};

export interface IClaimCloseoutContext {
    closed: boolean;
    voided?: boolean;
    /** Already humanised by the caller; shown as the terminal reason. */
    description?: string;
}

export interface IJourneyStageView {
    stage: JourneyStage;
    label: string;
    state: StageDisplayState;
    /** Phase name for the stage the cursor sits on; terminal reason for a completed closure. */
    caption?: string;
}

export interface ICurrentClaimProgress {
    /** Newest recorded event. */
    event?: IClaimProgressEvent;
    /** The event the stage nodes are anchored on (none when the newest tuple is unclassified). */
    anchor?: IClaimProgressEvent;
    /** Newest event is unclassified, so the card says so rather than guessing. */
    isUnclassified: boolean;
    eyebrow: string;
    stages: IJourneyStageView[];
}

/** Work blocked without the claim moving: Enter owns it, so the node reads Blocked, not Active. */
const isBlocked = (e: IClaimProgressEvent): boolean =>
    e.visualState === 'ACTION_REQUIRED_AFTER_RETRIES' ||
    e.visualState === 'SYSTEM_BLOCKED' ||
    (e.visualState === 'ENTER_ACTION_NEEDED' && e.phase === 'SUBMISSION_READINESS');

const stageStateFor = (
    stage: JourneyStage,
    anchor: IClaimProgressEvent | undefined,
    events: IClaimProgressEvent[],
    context: IClaimProgressContext,
): { state: StageDisplayState; caption?: string } => {
    const hasEvents = events.some(e => e.stage === stage);

    if (!anchor) {
        if (stage === 'ADDITIONAL_COVERAGE' && context.hasAdditionalCoverage === false) {
            return { state: 'NOT_APPLICABLE' };
        }
        return { state: 'UNKNOWN' };
    }

    const cursor = stageOrder(anchor.stage);
    const index = stageOrder(stage);

    if (index === cursor) {
        if (stage === 'CLOSURE') {
            if (anchor.visualState === 'COMPLETED') {
                return { state: 'COMPLETE', caption: anchor.terminalReason ?? 'Closed administratively' };
            }
            // A failed close leaves the final node blocked, never green.
            return { state: anchor.action === 'ERROR' ? 'BLOCKED' : 'ACTIVE', caption: PHASE_LABEL[anchor.phase] };
        }
        // Blocked outranks rework. Rework in this very stage (a resend, a correction) is still rework:
        // the stage was worked before and is being worked again, so it must not read as plain Active.
        if (isBlocked(anchor)) return { state: 'BLOCKED', caption: PHASE_LABEL[anchor.phase] };
        if (anchor.isRework && !anchor.reworkStage) {
            return { state: 'COMPLETE_REWORK_ACTIVE', caption: PHASE_LABEL[anchor.phase] };
        }
        return { state: 'ACTIVE', caption: PHASE_LABEL[anchor.phase] };
    }

    // Rework reopened an earlier stage (e.g. coding during a claim correction) while the cursor stays put.
    if (anchor.reworkStage === stage && hasEvents) {
        return { state: 'COMPLETE_REWORK_ACTIVE', caption: PHASE_LABEL[anchor.reworkPhase ?? 'CODING'] };
    }

    if (stage === 'ADDITIONAL_COVERAGE' && !hasEvents && context.hasAdditionalCoverage === false) {
        return { state: 'NOT_APPLICABLE' };
    }

    if (index < cursor) {
        // A stage the claim has moved past is complete only with evidence it was worked.
        return hasEvents ? { state: 'COMPLETE' } : { state: 'UNKNOWN' };
    }

    // The stage a wait is queued behind the current one reads "Next".
    if (stage === anchor.nextStage) return { state: 'PENDING', caption: 'Next' };

    // Ahead of the cursor: earlier completion is not erased by rework; otherwise not started.
    return hasEvents && stage !== 'CLOSURE' ? { state: 'COMPLETE' } : { state: 'PENDING' };
};

/**
 * REJECTED_CLAIM / DENIED_CLAIM / WAITING_FOR_PAYMENT carry no coverage level. History resolves them when it
 * can (the loop above). The claim's payer count resolves exactly one case: with a single active payer there is
 * only the primary one to wait on. A second policy proves nothing — a multi-payer claim can still be awaiting
 * primary adjudication before secondary billing — so `hasAdditionalCoverage: true` changes nothing here; only a
 * send or event in additional coverage (seen in history) moves these statuses there.
 */
const withContext = (
    event: IClaimProgressEvent | undefined,
    context: IClaimProgressContext,
): IClaimProgressEvent | undefined => {
    if (!event || !NEEDS_PAYER_CONTEXT.has(event.status)) return event;
    if (
        context.hasAdditionalCoverage === false &&
        (event.stage === 'PRIMARY_COVERAGE' || event.stage === 'UNCLASSIFIED')
    ) {
        return {
            ...event,
            stage: 'PRIMARY_COVERAGE',
            payerLevel: 'PRIMARY',
            label: event.status === 'WAITING_FOR_PAYMENT' ? 'Waiting for primary payer' : event.label,
        };
    }
    return event;
};

const ADDITIONAL_SUBMISSION_PHASES: OperationalPhase[] = [
    'TRANSMISSION_STATUS',
    'ORIGINAL_SUBMISSION',
    'ADDITIONAL_PAYER_BILLING',
];

const eyebrowFor = (event: IClaimProgressEvent | undefined): string => {
    if (!event) return 'Claim progress';
    if (event.stage === 'UNCLASSIFIED') {
        return event.phase === 'UNCLASSIFIED' ? 'Unclassified' : `Stage unknown / ${PHASE_LABEL[event.phase]}`;
    }
    if (
        event.stage === 'ADDITIONAL_COVERAGE' &&
        (event.payerLevel === 'SECONDARY' || event.payerLevel === 'TERTIARY') &&
        ADDITIONAL_SUBMISSION_PHASES.includes(event.phase)
    ) {
        const level = event.payerLevel === 'SECONDARY' ? 'Secondary' : 'Tertiary';
        return `${STAGE_EYEBROW_LABEL[event.stage]} / ${level} Submission`;
    }
    return `${STAGE_EYEBROW_LABEL[event.stage]} / ${PHASE_LABEL[event.phase]}`;
};

/**
 * The live event for a claim whose own closeout says it is closed. It keeps the newest history tuple
 * (so Source still shows the last workflow row) but is Closure / Completed, with the closeout's own
 * description as the terminal reason. A void is terminal too and cannot be reopened.
 */
const closeoutEvent = (
    newest: IClaimProgressEvent | undefined,
    closeout: IClaimCloseoutContext,
): IClaimProgressEvent => ({
    key: 'claim-closeout',
    date: newest?.date ?? '',
    status: newest?.status ?? '',
    step: newest?.step ?? '',
    action: newest?.action ?? '',
    stage: 'CLOSURE',
    phase: 'CLOSURE',
    visualState: 'COMPLETED',
    overlays: [],
    owner: 'NONE',
    payerLevel: 'TERMINAL',
    label: closeout.voided ? 'Claim voided' : 'Claim closed',
    detail: closeout.voided
        ? 'The claim is voided. A voided claim cannot be reopened.'
        : closeout.description
          ? `Closed out: ${closeout.description}`
          : 'The claim is closed out.',
    evidence: 'TUPLE_PROVEN',
    isRework: false,
    repeatCount: 1,
    stageFromHistory: false,
    terminalReason: closeout.voided ? 'Voided' : closeout.description || 'Closed',
});

/**
 * Current cursor + stage nodes. Never marks every node before a numeric index green and never
 * defaults an unknown tuple to Claim Preparation. When the newest tuple supports nothing the
 * tracker says so: every node is Unknown and earlier events stay in the feed as history only.
 */
export const deriveCurrentClaimProgress = (
    events: IClaimProgressEvent[],
    context: IClaimProgressContext = {},
): ICurrentClaimProgress => {
    // A closed or voided claim is Complete, even when the newest row is UNKNOWN (what a closed claim gets).
    const closedEvent = context.closeout?.closed ? closeoutEvent(events[0], context.closeout) : undefined;
    const event = closedEvent ?? withContext(events[0], context);
    const isUnclassified =
        !closedEvent && Boolean(event) && event.stage === 'UNCLASSIFIED' && event.visualState === 'UNCLASSIFIED';
    const anchor =
        closedEvent ??
        (isUnclassified
            ? undefined
            : withContext(
                  events.find(e => e.stage !== 'UNCLASSIFIED'),
                  context,
              ));

    const stages: IJourneyStageView[] = JOURNEY_STAGES.map(stage => ({
        stage,
        label: JOURNEY_STAGE_LABEL[stage],
        ...stageStateFor(stage, anchor, events, context),
    }));

    return { event, anchor, isUnclassified, eyebrow: eyebrowFor(event), stages };
};

/**
 * The raw Status / Step / Action tuple, verbatim (`-` for a slot the row did not carry). For an ERROR row
 * the attempt number follows, so "attempt 3 of 3" is visible where retries ran out.
 */
export const formatTuple = (status: string, step: string, action: string, attempt?: number): string =>
    `${[status, step, action].map(part => part || '-').join(' / ')}${attemptText(attempt)}`;

/** Automatic retries stop after three matching errors; a forced run can go past that. */
const AUTOMATIC_RETRY_LIMIT = 3;

/** "attempt 2 of 3" within the automatic limit; plain "attempt 4" beyond it (a forced run), never "4 of 3". */
const attemptText = (attempt?: number): string => {
    if (!attempt) return '';
    return attempt <= AUTOMATIC_RETRY_LIMIT
        ? ` (attempt ${attempt} of ${AUTOMATIC_RETRY_LIMIT})`
        : ` (attempt ${attempt})`;
};
